import {MouseEvent, useMemo} from "react";
import {useNavigate} from "react-router";
import {Marked} from "marked";
import {format} from "../i18n";
import {ALL_TRADEMARKS, TrademarkNotices} from "./TrademarkNotices";

const marked = new Marked({ gfm: true });

// A legal document's Markdown (src/i18n/en/legal), with its {placeholders} filled from values (src/legal.ts), shown
// styled like the rest of the site. A paragraph or list item with a placeholder whose value is null is left out: the
// EU and UK representatives, until they're appointed. The Markdown is part of the site's source, like its JSX, so it
// isn't sanitized: sanitizeHtml would drop its tables and send its links to new tabs. Each ends with the trademark notices,
// which Steinberg's ASIO and VST guidelines want on every legal page of a site about their products (components/TrademarkNotices.tsx).
export function LegalDocument({markdown, values}: { markdown: string, values: { [name: string]: string | null } }) {
    const navigate = useNavigate();
    const html = useMemo(() => {
        const missing = Object.keys(values).filter(x => values[x] === null);
        const kept = markdown.split(/\n{2,}/)
            .filter(block => !missing.some(x => block.includes(`{${x}}`)))
            .join('\n\n');
        const params = Object.fromEntries(Object.entries(values).filter((x): x is [string, string] => x[1] !== null));
        return (marked.parse(format(kept, params)) as string)
            .replace(/<h2>/g, '<h2 class="h5 mt-4">')
            .replace(/<h3>/g, '<h3 class="h6 mt-3">')
            .replace(/<table>/g, '<div class="table-responsive"><table class="table table-bordered table-sm">')
            .replace(/<\/table>/g, '</table></div>');
    }, [markdown, values]);

    // Links to the site's own pages stay in the app rather than loading it again.
    const followLink = (e: MouseEvent) => {
        const link = (e.target as HTMLElement).closest('a');
        const href = link?.getAttribute('href');
        if (href?.startsWith('/') && !href.startsWith('//') && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
            e.preventDefault();
            navigate(href);
        }
    };

    // Links handle the keyboard themselves: Enter on one is a click.
    return (
        <>
            <div onClick={followLink} dangerouslySetInnerHTML={{ __html: html }}/>
            <TrademarkNotices marks={ALL_TRADEMARKS} className="mt-5"/>
        </>
    );
}
